
import { useState, useEffect } from 'react';
import { TransportationDetails } from '@/types/itinerary';

/**
 * Hook to manage state with local storage persistence
 */
export const useLocalStorage = <T>(
  key: string,
  initialValue: T
): [T, (value: T | ((val: T) => T)) => void, () => void] => {
  // State to store our value
  const [storedValue, setStoredValue] = useState<T>(initialValue);

  // Load from localStorage on initial mount
  const loadValue = () => {
    try {
      const item = localStorage.getItem(key);
      
      if (item) {
        const parsedValue = JSON.parse(item) as T;
        
        // Special handling for date objects in transportation details
        if (key.includes('Travel')) {
          const transportDetails = parsedValue as unknown as TransportationDetails;
          if (transportDetails.departureDate) {
            transportDetails.departureDate = new Date(transportDetails.departureDate);
          }
          return transportDetails as unknown as T;
        }
        
        return parsedValue;
      }
      
      return initialValue;
    } catch (error) {
      console.error(`Error loading ${key} from localStorage:`, error);
      return initialValue;
    }
  };

  // Load from localStorage on component mount
  useEffect(() => {
    setStoredValue(loadValue());
  }, [key]);

  // Return a wrapped version of useState's setter function that
  // persists the new value to localStorage
  const setValue = (value: T | ((val: T) => T)) => {
    try {
      // Allow value to be a function so we have the same API as useState
      const valueToStore =
        value instanceof Function ? value(storedValue) : value;
        
      // Save state
      setStoredValue(valueToStore);
      
      // Save to localStorage
      localStorage.setItem(key, JSON.stringify(valueToStore));
    } catch (error) {
      console.error(`Error saving ${key} to localStorage:`, error);
    }
  };

  return [storedValue, setValue, loadValue];
};
